import { action, makeObservable, observable } from "mobx";
import AuthStore from "../Auth/authStore";
import {
  TransactionsApi,
  BaseTransactionDetailsDto,
  RxDto,
  CreatePatientTransactionDto,
  CreateInsuranceTransactionDto,
  PayerDto,
  GetInsuranceTransactionDto,
  GroupedDataDto,
  ExportBillingData,
  UpdateInsuranceTransactionDto,
  UpdateExportStatusDto,
  DocumentDto,
  PaginatedGetBankTransferDto,
  PaginatedBaseTransactionDetailsDto,
  PaginatedGetRechnungDto,
  CreateRechnungDto,
  GetRechnungDetailsDto,
  CreateBankTransferDto,
  UpdateRechnungDto,
  ProcessedCopaymentBillingDto,
  DebitPermissionDto,
  UploadDocumentDto,
  ProcessedPaperBillingDto,
  MergeDocumentsRequestDto,
  MergeDocumentsResponseDto,
  CreateServicesTransactionDto,
  CreateServiceRechnungDto,
  BillingAnalyticsQueryDto,
  SEPAExportResultDto,
  RxPreviewDto,
  ProcessedCorrectionBillingDto,
  PatientBillInfoDto,
  UpdateSelectedLineItemsRequest,
} from "../../api-client";
import notificationStore from "../Notification/notificationStore";
import { transactionMethods } from "../../components/Transactions/Kasse/v2/types/kasse.types";
import { SEPACreditTransferRequest } from "./interfaces/SEPACreditTransfer.interface";
import { BillingStatus } from "../../components/Transactions/Insurance/Base/pages/InsurancePageLayout";
import {
  BillingTypeEnum,
  PaginatedGetInsuranceTransaction,
  SearchTransaction,
} from "../../components/Transactions/Insurance/Base/types/insuranceBills.types";
import {
  ProcessingCode,
  TestTypeInterface,
} from "../../components/Transactions/Insurance/DirectBilling/Edit/types/TestTypes.interface";
import { BaseStore } from "../BaseStore";
import { SelectedRxs } from "../../components/Transactions/Insurance/CorrectionBilling/New/types/correction.interface";

export default class TransactionsStore extends BaseStore<TransactionsApi> {
  public authStore: AuthStore;

  public constructor(api: TransactionsApi, authStore: AuthStore) {
    super(api);
    this.authStore = authStore;
    makeObservable(this, {
      lastUpdate: observable,
      transactionRefresh: observable,
      unpaidBills: observable,
      setUnpaidBillsForPatient: action,
      setTransactionRefresh: action,
      createTransaction: action,
      createServicesTransaction: action,
      noShowInvoiceTransaction: action,
    });
  }
  public lastUpdate = new Date();

  public transactionRefresh: boolean = false;

  public unpaidBills: {
    rxId: string;
    rxNumber: string;
    tarifGroupId: string;
    dueAmount: number;
  }[] = [];

  public setUnpaidBillsForPatient = (
    value: {
      rxId: string;
      rxNumber: string;
      tarifGroupId: string;
      dueAmount: number;
    }[],
  ) => {
    this.unpaidBills = value;
  };

  public setTransactionRefresh = (val: boolean) => {
    this.transactionRefresh = val;
  };

  public createTransaction = async (
    creatDTO: CreatePatientTransactionDto | CreateInsuranceTransactionDto,
  ): Promise<BaseTransactionDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.createTransaction(creatDTO);
      this.lastUpdate = new Date();
      notificationStore.showMessage(
        "Transaktion erfolgreich erstellt",
        "success",
      );
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen der Transaktion: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public createServicesTransaction = async (
    isGiftCertificate: boolean,
    creatDTO: CreateServicesTransactionDto,
  ): Promise<BaseTransactionDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.createServicesTransaction(
        creatDTO,
        isGiftCertificate,
      );
      this.lastUpdate = new Date();
      notificationStore.showMessage(
        "Transaktion erfolgreich erstellt",
        "success",
      );
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen der Transaktion: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public noShowInvoiceTransaction = async (
    appointmentId: string,
    creatDTO: CreateRechnungDto,
  ): Promise<GetRechnungDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.noShowInvoiceTransaction(
        appointmentId,
        creatDTO,
      );
      this.lastUpdate = new Date();
      notificationStore.showMessage(
        "Transaktion erfolgreich erstellt",
        "success",
      );
      return data || [];
    } catch (error) {
      console.error("Error creating transaction:", error);
      notificationStore.showMessage(
        "Error creating transaction",
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * @deprecated
   * replaced with the usePatientTransactions hook inside the transaction.queries.ts file
   */
  public getTransactionByPatientId = async (
    id: number,
  ): Promise<BaseTransactionDetailsDto[]> => {
    const { data } = (await this.apiUnwrapped.getAllTxsForPatient(id)) as any;
    return data || [];
  };

  /**
   * @deprecated
   * replaced with the usePatientBillsInfo hook inside the transaction.queries.ts file
   */
  public patientBillInfo = async (
    id: number,
  ): Promise<PatientBillInfoDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.patientBillInfo(id)) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching paid bills:", error);
      notificationStore.showMessage(
        `Error fetching paid bills for patient ID ${id}:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getDuePaymentByRxId = async (
    id: string,
  ): Promise<{
    dueAmount: number;
    matchAppointments: boolean;
    totalAmountPaid: number;
    isTimeSlotZero: boolean;
  }> => {
    try {
      const { data } = (await this.apiUnwrapped.getDuePaymentByRxId(id)) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching due payment:", error);
      notificationStore.showMessage(
        `Error fetching due payment for rx number ${id}:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getBadgeUnpaidBillsByPatientId = async (
    id: number,
  ): Promise<
    {
      rxId: string;
      rxNumber: string;
      tarifGroupId: string;
      dueAmount: number;
    }[]
  > => {
    try {
      const { data } = (await this.apiUnwrapped.badgesForUnpaidBills(
        id,
      )) as any;
      this.setUnpaidBillsForPatient(data);
      // console.log("shhdhhdhdhdh", data);
      return data || [];
    } catch (error) {
      console.error("Error fetching reminder badge for unpaid bills:", error);
      notificationStore.showMessage(
        `Error fetching reminder badge for unpaid bills for patient ID ${id}:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getBillingCentersFromPayer = async (
    payerIk: string,
    accountId: string,
  ): Promise<PayerDto> => {
    try {
      const { data } = (await this.apiUnwrapped.getBillingCentersFromPayer(
        payerIk,
        accountId,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching payers and billing centers:", error);
      notificationStore.showMessage(
        `Für Versicherer Nummer ${payerIk} wurde kein Zahler gefunden`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllInsuranceBill = async (
    billStatus: BillingStatus,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetInsuranceTransaction> => {
    try {
      const {
        page = 1,
        limit = 15,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } = await this.apiUnwrapped.getAllInsuranceTransactions(
        billStatus,
        limit,
        page,
        startDate,
        endDate,
        searchStr,
      );

      return data || [];
    } catch (error) {
      console.error("Error fetching insurance bills:", error);
      notificationStore.showMessage(
        `Error fetching insurance bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllPaperInsuranceBill = async (
    billStatus: BillingStatus,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetInsuranceTransaction> => {
    try {
      const {
        page = 1,
        limit = 15,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } = await this.apiUnwrapped.getAllPaperInsuranceTransactions(
        billStatus,
        limit,
        page,
        startDate,
        endDate,
        searchStr,
      );
      return data || [];
    } catch (error) {
      console.error("Error fetching insurance bills:", error);
      notificationStore.showMessage(
        `Error fetching post insurance bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllCopaymentInsuranceBill = async (
    billStatus: BillingStatus,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetInsuranceTransaction> => {
    try {
      const {
        page = 1,
        limit = 15,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } =
        await this.apiUnwrapped.getAllCopaymentInsuranceTransactions(
          billStatus,
          limit,
          page,
          startDate,
          endDate,
          searchStr,
        );
      return data || [];
    } catch (error) {
      console.error("Error fetching insurance bills:", error);
      notificationStore.showMessage(
        `Error fetching copayment insurance bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllCorrectedInsuranceBill = async (
    billStatus: BillingStatus,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetInsuranceTransaction> => {
    try {
      const {
        page = 1,
        limit = 15,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } =
        await this.apiUnwrapped.getAllCorrectionInsuranceTransactions(
          billStatus,
          limit,
          page,
          startDate,
          endDate,
          searchStr,
        );
      return data || [];
    } catch (error) {
      console.error("Error fetching insurance bills:", error);
      notificationStore.showMessage(
        `Error fetching corrected insurance bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllLineItemCorrectedInsuranceBill = async (
    billStatus: BillingStatus,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetInsuranceTransaction> => {
    try {
      const {
        page = 1,
        limit = 15,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } =
        await this.apiUnwrapped.getAllLineItemCorrectionInsuranceTransactions(
          billStatus,
          limit,
          page,
          startDate,
          endDate,
          searchStr,
        );
      return data || [];
    } catch (error) {
      console.error("Error fetching line item correction bills:", error);
      notificationStore.showMessage(
        `Error fetching line item correction bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getSingleRxForIndirectBillsForInsurance = async (
    rxNumber: string,
  ): Promise<any> => {
    try {
      const { data } =
        (await this.apiUnwrapped.getSingleRxForIndirectBillsForInsurance(
          rxNumber,
        )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching insurance bills:", error);
      notificationStore.showMessage(
        "Das Rezept konnte nicht zur Abrechnung hinzugefügt werden",
        "error",
        error,
      );
      throw error;
    }
  };

  public directBillingAllInfo = async (
    accountId: string,
  ): Promise<GroupedDataDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getDataPacketForDirectBilling(
        accountId,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching direct billing data:", error);
      notificationStore.showMessage(
        `Error fetching direct billing data:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public PaperBillingAllInfo = async (
    accountId: string,
  ): Promise<ProcessedPaperBillingDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getDataPacketForPaperBilling(
        accountId,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching direct billing data:", error);
      notificationStore.showMessage(
        `Error fetching direct billing data:`,
        "error",
        error,
      );
      throw error;
    }
  };

  //*************************** Correction Billing *********************************/

  public resolveTransactionNumber = async (
    accountId: string,
    transactionNumber: string,
  ): Promise<any> => {
    try {
      const { data } = (await this.apiUnwrapped.resolveCorrectionBillNumber(
        accountId,
        transactionNumber,
      )) as any;
      return data || [];
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Fehler beim Auflösen der Transaktionsnummer";
      notificationStore.showMessage(msg, "error", error);
      throw error;
    }
  };

  public getRxPreviewData = async (
    transactionId: string,
  ): Promise<RxPreviewDto> => {
    try {
      const { data } = (await this.apiUnwrapped.getRxPreviewsForCorrectionBill(
        transactionId,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching RX preview data:", error);
      notificationStore.showMessage(
        `Error fetching RX preview data:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public prepareCorrectionBillsFromRxs = async (
    body: SelectedRxs,
  ): Promise<ProcessedCorrectionBillingDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.prepareCorrectionBillsFromRxs(
        body,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error preparing correction bills from RXs:", error);
      notificationStore.showMessage(
        `Error preparing correction bills from RXs:`,
        "error",
        error,
      );
      throw error;
    }
  };

  //*************************** Line Item Correction Billing *********************************/

  public getLineItemsForRx = async (rxId: string): Promise<any[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getLineItemsForRx(rxId)) as any;
      return data || [];
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Fehler beim Laden der Positionen";
      notificationStore.showMessage(msg, "error", error);
      throw error;
    }
  };

  public updateSelectedLineItems = async (
    transactionId: string,
    rxId: string,
    body: UpdateSelectedLineItemsRequest,
  ): Promise<{ amount?: number }> => {
    try {
      const { data } = (await this.apiUnwrapped.updateSelectedLineItems(
        transactionId,
        rxId,
        body,
      )) as any;
      return data || {};
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Fehler beim Aktualisieren der ausgewählten Positionen";
      notificationStore.showMessage(msg, "error", error);
      throw error;
    }
  };

  public refreshBillAmounts = async (billId: string) => {
    try {
      const { data } = (await this.apiUnwrapped.refreshAmount(billId)) as any;
      return data || [];
    } catch (error) {
      console.error("Error refreshing bill amounts:", error);
      notificationStore.showMessage(
        "Error refreshing bill amounts",
        "error",
        error,
      );
      throw error;
    }
  };

  public resolveRxId = async (rxNumber: string) => {
    try {
      const { data } = (await this.apiUnwrapped.resolveRx(rxNumber)) as any;
      return data || [];
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Fehler beim Auflösen der RX-ID";
      notificationStore.showMessage(msg, "error", error);
      throw error;
    }
  };

  public getBillingDataForEditing = async (
    billId: string[],
    billingType: BillingTypeEnum,
  ): Promise<ExportBillingData[]> => {
    // An empty list is truthy in the generated client, so it would go out as a
    // request without billId and come back as a raw 400. Nothing to load here.
    if (!billId || billId.length === 0) {
      return [];
    }
    try {
      const { data } = (await this.apiUnwrapped.getBillingDataForEditing(
        billId,
        billingType,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching billing data for editing:", error);
      notificationStore.showMessage(
        "Die Rechnungsdaten konnten nicht geladen werden",
        "error",
        error,
      );
      throw error;
    }
  };

  public setStatusOpenToFinished = async (billId: string): Promise<any> => {
    try {
      await this.apiUnwrapped.setStatusOpenToFinished(billId);
    } catch (error) {
      console.error("Error updating status:", error);
      notificationStore.showMessage(
        "Fehler beim Aktualisieren des Status",
        "error",
        error,
      );
      throw error;
    }
  };

  public exportData = async (
    exportBillingData: ExportBillingData[],
    testTypeData: TestTypeInterface,
    processingCode: ProcessingCode,
  ): Promise<any> => {
    try {
      const { data } = await this.apiUnwrapped.exportInsuranceBills({
        exportBillingData,
        testTypeData,
        processingCode,
      });
      const errorMessage = data.find((item: string) =>
        item.toLowerCase().includes("error"),
      );
      if (errorMessage) {
        notificationStore.showMessage(errorMessage, "error");
      } else {
        notificationStore.showMessage(
          "Abrechnungsdaten wurden erfolgreich digital an den Kostenträger exportiert.",
          "success",
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      notificationStore.showMessage(
        "Fehler beim Exportieren der Daten",
        "error",
        error,
      );
      throw error;
    }
  };

  public updateExportAndBillingStatus = async (
    billId: string,
    data: UpdateExportStatusDto,
  ): Promise<any> => {
    try {
      await this.apiUnwrapped.updateExportAndBillingStatus(billId, data);
    } catch (error) {
      console.error("Error updating export status:", error);
      notificationStore.showMessage(
        "Error updating export status",
        "error",
        error,
      );
      throw error;
    }
  };

  public updateExportBillingStatusAndRxStatus = async (
    billId: string,
    data: UpdateExportStatusDto,
    rxIds: string[],
  ): Promise<any> => {
    try {
      // console.log("RXIDS", rxIds);
      await this.apiUnwrapped.updateExportBillingStatusAndRxStatus(
        billId,
        data,
        rxIds,
      );
    } catch (error) {
      console.error("Error updating export status:", error);
      notificationStore.showMessage(
        "Error updating export status",
        "error",
        error,
      );
      throw error;
    }
  };

  public printBillingData = async (
    billId: string,
    incomingData: UpdateInsuranceTransactionDto,
  ): Promise<GetInsuranceTransactionDto> => {
    try {
      const { data } = await this.apiUnwrapped.printDirectBillingDocument(
        billId,
        incomingData,
      );
      return data || [];
    } catch (error) {
      console.error("Error submitting billing data for printing:", error);
      notificationStore.showMessage(
        "Error submitting billing data for printing",
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * Regenerate the PDFs for an insurance transaction (Export Module)
   */
  public regenerateInsuranceTransactionPdfs = async (
    billId: string,
    pdfDate: string,
  ): Promise<GetInsuranceTransactionDto> => {
    try {
      const { data } =
        await this.apiUnwrapped.regenerateInsuranceTransactionPdfs(
          billId,
          pdfDate,
        );
      notificationStore.showMessage(
        "PDFs erfolgreich neu generiert",
        "success",
      );
      return data || [];
    } catch (error) {
      console.error(
        "Error regenerating PDFs for insurance transaction:",
        error,
      );
      notificationStore.showMessage(
        "Error regenerating PDFs for insurance transaction",
        "error",
        error,
      );
      throw error;
    }
  };

  public printInsuranceCopaymentDocuments = async (
    billId: string,
    incomingData: UpdateInsuranceTransactionDto,
  ): Promise<GetInsuranceTransactionDto> => {
    try {
      const { data } = await this.apiUnwrapped.printInsuranceCopaymentDocuments(
        billId,
        incomingData,
      );
      return data || [];
    } catch (error) {
      console.error("Error submitting billing data for printing:", error);
      notificationStore.showMessage(
        "Error submitting billing data for printing",
        "error",
        error,
      );
      throw error;
    }
  };

  public submitEditedBillingData = async (
    billId: string,
    data: UpdateInsuranceTransactionDto,
  ): Promise<UpdateInsuranceTransactionDto> => {
    try {
      await this.apiUnwrapped.updateInsuranceTransaction(billId, data);
      //notificationStore.showMessage("Daten erfolgreich gespeichert", "success");
      return data || [];
    } catch (error) {
      console.error("Error submitting edited billing data:", error);
      notificationStore.showMessage(
        "Error submitting edited billing data",
        "error",
        error,
      );
      throw error;
    }
  };

  public printPaperBillingDocument = async (
    billId: string,
    incomingData: UpdateInsuranceTransactionDto,
  ): Promise<GetInsuranceTransactionDto> => {
    try {
      const { data } = await this.apiUnwrapped.printPaperBillingDocument(
        billId,
        incomingData,
      );
      return data || [];
    } catch (error) {
      console.error("Error submitting billing data for printing:", error);
      notificationStore.showMessage(
        "Error submitting billing data for printing",
        "error",
        error,
      );
      throw error;
    }
  };

  public downloadPdf = async (documentId: string): Promise<DocumentDto> => {
    try {
      const { data } =
        await this.apiUnwrapped.downloadDirectBillingDocument(documentId);
      return data || {};
    } catch {
      notificationStore.showMessage(
        "Fehler beim Herunterladen des PDFs",
        "error",
      );
      throw new Error("Error downloading PDF");
    }
  };

  public mergeDirectBillingDocuments = async (
    payload: MergeDocumentsRequestDto,
  ): Promise<MergeDocumentsResponseDto> => {
    try {
      const { data } =
        await this.apiUnwrapped.mergeDirectBillingDocuments(payload);
      return data || ({} as MergeDocumentsResponseDto);
    } catch (error) {
      console.error("Error merging billing documents:", error);
      notificationStore.showMessage(
        "Fehler beim Zusammenführen der PDFs",
        "error",
        error,
      );
      throw error;
    }
  };

  public softDeleteTransaction = async (
    transactionId: string,
  ): Promise<{ message: string }> => {
    try {
      const { message } =
        (await this.apiUnwrapped.softDeleteInsuranceTransaction(
          transactionId,
        )) as any;
      notificationStore.showMessage(
        "Die Rechnung wurde erfolgreich gelöscht.",
        "success",
      );
      return message || [];
    } catch (error) {
      console.error(
        "For some reason the transaction is not deleted. This message is coming from transaction store",
        error,
      );
      notificationStore.showMessage(
        "Fehler beim Löschen der Transaktion",
        "error",
        error,
      );
      throw error;
    }
  };

  // public getPatientAndRxDetails = async (name: string): Promise<any> => {
  //   try {
  //     const { data } = (await this.apiUnwrapped.getPatientRx(name)) as any;
  //     // console.log("PatientData: With RX for SPOTLIGHT Search FROM STORE", data);
  //     return data || [];
  //   } catch (error) {
  //     console.error("Error fetching patient and rx details:", error);
  //     notificationStore.showMessage(
  //       `Error fetching patient and rx details:`,
  //       "error",
  //       error,
  //     );
  //     throw error;
  //   }
  // };

  public getTransactionsByType = async (
    accountId: string,
    transactionType: transactionMethods,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedBaseTransactionDetailsDto> => {
    try {
      const {
        page = 1,
        limit = 1,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } = await this.apiUnwrapped.getAllTransactions(
        accountId,
        transactionType,
        limit,
        page,
        startDate,
        endDate,
        searchStr,
      );
      return data || [];
    } catch (error) {
      console.error("Error fetching transactions:", error);
      notificationStore.showMessage(
        `Error fetching transactions:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllunexportedLastSchriftTransactionsForAccount = async (
    accountId: string,
  ): Promise<BaseTransactionDetailsDto[]> => {
    try {
      const { data } =
        await this.apiUnwrapped.getAllUnexportedLastschriftTransactions(
          accountId,
        );
      return data || [];
    } catch (error) {
      console.error(
        "Error fetching unexported Lastschrift transactions:",
        error,
      );
      notificationStore.showMessage(
        `Error fetching unexported Lastschrift transactions:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getAllBankTransfer = async (
    accountId: string,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetBankTransferDto> => {
    try {
      const {
        page = 1,
        limit = 1,
        startDate,
        endDate,
        searchStr,
      } = searchTransactionQuery;
      const { data } = await this.apiUnwrapped.getAllBankTransfers(
        accountId,
        limit,
        page,
        startDate,
        endDate,
        searchStr,
      );
      return data || [];
    } catch (error) {
      console.error("Error fetching bank transfers:", error);
      notificationStore.showMessage(
        `Error fetching bank transfers:`,
        "error",
        error,
      );
      throw error;
    }
  };

  // @deprecated — replaced by getUnpaidRechnungen, getPaidRechnungen, getBillReminderEligibleRechnungen
  // public getAllRechnung = async (
  //   accountId: string,
  //   searchTransactionQuery: SearchTransaction,
  // ): Promise<PaginatedGetRechnungDto> => {
  //   try {
  //     const {
  //       page = 1,
  //       limit = 1,
  //       startDate,
  //       endDate,
  //       searchStr,
  //     } = searchTransactionQuery;
  //     const { data } = await this.apiUnwrapped.getAllRechnung(
  //       accountId,
  //       limit,
  //       page,
  //       startDate,
  //       endDate,
  //       searchStr,
  //     );
  //     return data || [];
  //   } catch (error) {
  //     console.error("Error fetching rechnungen:", error);
  //     notificationStore.showMessage(
  //       `Error fetching rechnungen:`,
  //       "error",
  //       error,
  //     );
  //     throw error;
  //   }
  // };

  /**
   * @deprecated
   * @param pid
   * @returns
   */
  public patientRechnunInfo = async (
    pid: number,
  ): Promise<GetRechnungDetailsDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.patientRechnungInfo(
        pid,
      )) as any;
      return data || [];
    } catch (error) {
      notificationStore.showMessage((error as Error).message, "error");
      throw error;
    }
  };

  // @deprecated — replaced by getBillReminderEligibleRechnungen
  // public getAllRechnungWithBillReminder = async (
  //   accountId: string,
  //   searchTransactionQuery: SearchTransaction,
  // ): Promise<PaginatedGetRechnungDto> => {
  //   try {
  //     const {
  //       page = 1,
  //       limit = 1,
  //       startDate,
  //       endDate,
  //       searchStr,
  //     } = searchTransactionQuery;
  //     const { data } = await this.apiUnwrapped.getAllRechnungWithBillReminders(
  //       accountId,
  //       limit,
  //       page,
  //       startDate,
  //       endDate,
  //       searchStr,
  //     );
  //     return data || [];
  //   } catch (error: any) {
  //     console.error("Problem Getting Mahnung:", error);
  //     let errorMessage = error.response?.data?.message || error.message;
  //     notificationStore.showMessage(
  //       `Problem Getting Mahnung: ${errorMessage}`,
  //       "error",
  //       error,
  //     );
  //     throw error;
  //   }
  // };

  /**
   * Fetches paginated unpaid Rechnungen (NOT_PAID or PARTIAL) for an account.
   * Some of these may also be Mahnung-eligible.
   */
  public getUnpaidRechnungen = async (
    accountId: string,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetRechnungDto> => {
    const {
      page = 1,
      limit = 15,
      startDate,
      endDate,
      searchStr,
    } = searchTransactionQuery;
    const { data } = await this.apiUnwrapped.getUnpaidRechnung(
      accountId,
      limit,
      page,
      startDate,
      endDate,
      searchStr,
    );
    return data;
  };

  /**
   * Fetches paginated fully paid Rechnungen for an account.
   */
  public getPaidRechnungen = async (
    accountId: string,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetRechnungDto> => {
    const {
      page = 1,
      limit = 15,
      startDate,
      endDate,
      searchStr,
    } = searchTransactionQuery;
    const { data } = await this.apiUnwrapped.getPaidRechnung(
      accountId,
      limit,
      page,
      startDate,
      endDate,
      searchStr,
    );
    return data;
  };

  /**
   * Fetches paginated Rechnungen that are eligible for a Mahnung (bill reminder).
   * These are unpaid invoices where the 14-day waiting period has elapsed
   * and a new reminder can be issued.
   */
  public getBillReminderEligibleRechnungen = async (
    accountId: string,
    searchTransactionQuery: SearchTransaction,
  ): Promise<PaginatedGetRechnungDto> => {
    const {
      page = 1,
      limit = 15,
      startDate,
      endDate,
      searchStr,
    } = searchTransactionQuery;
    const { data } = await this.apiUnwrapped.getAllRechnungWithBillReminders(
      accountId,
      limit,
      page,
      startDate,
      endDate,
      searchStr,
    );
    return data;
  };

  //   // public getRechnungByPatientId = async (pid: number): Promise<GetRechnungDetailsDto[]> => {
  //   try {
  //     const { data } = await this.apiUnwrapped.getRechnungByPatientId(pid) as any;
  //     return data || [];
  //   } catch (error) {
  //     notificationStore.showMessage("error", (error as Error).message);
  //     throw error;
  //   }
  // }
  public getDebitPermissionsByPatient = async (
    pid: number,
  ): Promise<DebitPermissionDto[]> => {
    try {
      const { data } = (await this.apiUnwrapped.getDebitPermissionsForPatient(
        pid,
      )) as any;
      return data || [];
    } catch (error) {
      notificationStore.showMessage((error as Error).message, "error");
      throw error;
    }
  };

  public createOrUpdateDebitPermission = async (
    patientId: number,
    dto: DebitPermissionDto,
  ) => {
    try {
      const { data } = (await this.apiUnwrapped.createOrUpdateDebitPermission(
        patientId,
        dto,
      )) as any;
      notificationStore.showMessage(
        "Abbuchungsberechtigung erfolgreich aktualisiert",
        "success",
      );
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Aktualisieren der Berechtigung: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public softDeleteDebitPermission = async (debitPermissionId: string) => {
    try {
      const { message } = (await this.apiUnwrapped.softDeleteDebitPermission(
        debitPermissionId,
      )) as any;
      notificationStore.showMessage(
        "Abbuchungserlaubnis erfolgreich gelöscht",
        "success",
      );
      return message || "";
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Löschen der Berechtigung: ${errorMessage}`,
        "error",
        error,
      );
    }
  };

  public getUniquePairsForPatient = async (patientId: number) => {
    try {
      const { data } = (await this.apiUnwrapped.getUniquePairsForPatient(
        patientId,
      )) as any;
      // console.log("ausuduuduuuf", data);
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      throw error;
    }
  };

  public generateDebitPermissionPdf = async (debitPermissionId: string) => {
    try {
      const { data } = (await this.apiUnwrapped.generateDebitPermissionPdf(
        debitPermissionId,
      )) as any;
      notificationStore.showMessage("PDF erfolgreich erstellt!", "success");
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen des PDF: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public uploadSignedDocument = async (
    debitPermissionId: string,
    body: UploadDocumentDto,
  ) => {
    try {
      const { data } = (await this.apiUnwrapped.uploadSignedDocument(
        debitPermissionId,
        body.docuType,
        body.ownerType,
        body.ownerId,
        body.file,
        body.makeAccessible,
        body.rxId,
      )) as any;
      notificationStore.showMessage(
        "Document uploaded successfully!",
        "success",
      );
      return data || [];
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to upload Document";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public uploadRxScanToExistingBill = async (
    transactionId: string,
    file: File,
    ownerId: string,
  ): Promise<DocumentDto | undefined> => {
    try {
      const { data } = (await this.apiUnwrapped.uploadRxScanToExistingBill(
        transactionId,
        "Rezept" as any,
        "Payer" as any,
        ownerId,
        file,
        true,
      )) as any;
      notificationStore.showMessage(
        "Rezept-Scan erfolgreich hochgeladen!",
        "success",
      );
      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Fehler beim Hochladen des Rezept-Scans";
      notificationStore.showMessage(errorMessage, "error", error);
      return undefined;
    }
  };

  public createRechnung = async (
    creatDto: CreateRechnungDto,
  ): Promise<GetRechnungDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.createRechnung(creatDto);
      this.lastUpdate = new Date();
      notificationStore.showMessage("Rechnung erfolgreich erstellt", "success");
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen der Rechnungen: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public createServiceRechnung = async (
    creatDto: CreateServiceRechnungDto,
  ): Promise<GetRechnungDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.createServiceRechnung(creatDto);
      this.lastUpdate = new Date();
      notificationStore.showMessage("Rechnung erfolgreich erstellt", "success");
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen der Rechnungen: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public softDeleteRechnung = async (
    rechnungId: string,
  ): Promise<{ message: string }> => {
    try {
      const { data } = (await this.apiUnwrapped.softDeleteRechnung(
        rechnungId,
      )) as any;
      notificationStore.showMessage("Rechnung erfolgreich gelöscht", "success");
      return data || [];
    } catch (error) {
      console.error(
        "For some reason the rechnung is not deleted. This message is coming from transaction store",
        error,
      );
      notificationStore.showMessage(
        "Fehler beim Löschen der Rechnung",
        "error",
        error,
      );
      throw error;
    }
  };

  public softDeleteLastSchrift = async (
    lastSchriftId: string,
    deleteRechnung: boolean = true,
  ) => {
    try {
      const { data } = await this.apiUnwrapped.softDeleteLastSchrift(
        lastSchriftId,
        deleteRechnung,
      );
      this.lastUpdate = new Date();

      notificationStore.showMessage(
        data.message || "LastSchrift erfolgreich gelöscht",
        "success",
      );

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Löschen der LastSchrift: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public bulkSoftDeleteLastSchrifts = async (
    lastSchriftIds: string[],
    deleteAssociatedRechnungen: boolean = true,
  ) => {
    try {
      const { data } = await this.apiUnwrapped.bulkSoftDeleteLastSchrifts({
        lastSchriftIds,
        deleteAssociatedRechnungen,
      });
      this.lastUpdate = new Date();

      // Show appropriate success/warning message
      if (data.errors && data.errors.length > 0) {
        notificationStore.showMessage(
          `${data.deletedCount} LastSchrift-Transaktionen gelöscht. ${data.errors.length} Fehler aufgetreten.`,
          "warning",
        );
      } else {
        notificationStore.showMessage(
          `${data.deletedCount} LastSchrift-Transaktionen erfolgreich gelöscht`,
          "success",
        );
      }

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Bulk-Löschen der LastSchrift-Transaktionen: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public createBillReminder = async (rechnungId: string): Promise<any> => {
    try {
      const { data } =
        await this.apiUnwrapped.createBillReminderForRechnung(rechnungId);
      notificationStore.showMessage("Mahnung erfolgreich erstellt", "success");
      return data || [];
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen der Mahnung: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  //   // public softDeleteRechnung = async (rechnungId: string): Promise<{ message: string }> => {
  //   try {
  //     const { data } = await this.apiUnwrapped.softDeleteRechnung(rechnungId) as any;
  //     notificationStore.showMessage("Rechnung erfolgreich gelöscht", "success");
  //     return data || [];
  //   }
  //   catch (error) {
  //     console.error('For some reason the rechnung is not deleted. This message is coming from transaction store', error);
  //     notificationStore.showMessage("Fehler beim Löschen der Rechnung", "error", error);
  //     throw error;
  //   }
  // }
  public createBankTransfer = async (
    creatDto: CreateBankTransferDto,
  ): Promise<void> => {
    try {
      await this.apiUnwrapped.createBankTransferForRechnung(creatDto);
      this.lastUpdate = new Date();
      notificationStore.showMessage(
        "Bank Transfer erfolgreich erstellt",
        "success",
      );
      // return data || [];
    } catch (error) {
      notificationStore.showMessage((error as Error).message, "error");
      throw error;
    }
  };

  public updateRechnung = async (
    id: string,
    updateData: UpdateRechnungDto,
  ): Promise<GetRechnungDetailsDto> => {
    try {
      const { data } = await this.apiUnwrapped.updateRechnungById(
        id,
        updateData,
      );
      this.lastUpdate = new Date();
      notificationStore.showMessage(
        "Rechnung erfolgreich aktualisiert",
        "success",
      );
      return data || [];
    } catch (error) {
      notificationStore.showMessage((error as Error).message, "error");
      throw error;
    }
  };

  /**
   * @deprecated
   * @param rxId
   * @returns
   */
  public getRechnungForRx = async (rxId: string): Promise<any> => {
    try {
      const { data } = await this.apiUnwrapped.rechnungsForRx(rxId);
      return data || null;
    } catch (error) {
      notificationStore.showMessage((error as Error).message, "error");
      throw error;
    }
  };

  /**
   * Get all rechnungen for a specific RX
   * @param rxId
   * @returns Array of GetRechnungDetailsDto
   */
  public getAllRechnungenForRx = async (
    rxId: string,
  ): Promise<GetRechnungDetailsDto[]> => {
    try {
      const { data } = await this.apiUnwrapped.rechnungsForRx(rxId);
      return data || [];
    } catch (error) {
      console.error("Error fetching rechnungen for RX:", error);
      // Don't show error notification for empty results
      return [];
    }
  };

  public getRxsForCopaymentBill = async (
    accountId: string,
  ): Promise<ProcessedCopaymentBillingDto[]> => {
    try {
      const { data } =
        (await this.apiUnwrapped.getDataPacketForCopaymentBilling(
          accountId,
        )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching copayment bills:", error);
      notificationStore.showMessage(
        `Error fetching copayment bills:`,
        "error",
        error,
      );
      throw error;
    }
  };

  public getBillingAnalytics = async (
    billingAnalyticsQuery: BillingAnalyticsQueryDto,
  ): Promise<any> => {
    try {
      const {
        startDate,
        endDate,
        accountId,
        payerId,
        billStatus,
        billType,
        userId,
      } = billingAnalyticsQuery;
      const { data } = (await this.apiUnwrapped.getExecutiveDashboard(
        startDate,
        endDate,
        accountId,
        payerId,
        billStatus,
        billType,
        userId,
      )) as any;
      return data || [];
    } catch (error) {
      console.error("Error fetching billing analytics:", error);
      notificationStore.showMessage(
        `Error fetching billing analytics:`,
        "error",
        error,
      );
      throw error;
    }
  };

  // SEPA Export functionality for LastSchrift transactions
  public getUnexportedLastSchrifts = async (accountId?: string) => {
    try {
      const { data } =
        await this.apiUnwrapped.getUnexportedLastSchrifts(accountId);
      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Abrufen der nicht exportierten LastSchrift-Transaktionen: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public exportLastSchriftsToSEPA = async (
    lastSchriftIds: string[],
    exportOptions: {
      messageId: string;
      creditorName: string;
      creditorIBAN: string;
      creditorBIC?: string;
      creditorId: string;
    },
  ) => {
    try {
      const { data } = await this.apiUnwrapped.exportLastSchriftsToSEPA({
        lastSchriftIds,
        exportOptions,
      });
      this.lastUpdate = new Date();

      notificationStore.showMessage(
        `${data.exportedCount} LastSchrift-Transaktionen erfolgreich exportiert`,
        "success",
      );

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim SEPA-Export: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public exportLastSchriftsByAccount = async (
    accountId: string,
  ): Promise<SEPAExportResultDto> => {
    try {
      const { data } = await this.apiUnwrapped.exportLastSchriftsByAccount({
        accountId,
      });
      this.lastUpdate = new Date();

      // Use the message from the API response if available
      if (data.message) {
        notificationStore.showMessage(data.message, "success");
      } else if (!data || data.exportedCount === 0) {
        notificationStore.showMessage(
          `Keine nicht exportierten LastSchrift-Transaktionen für diesen Account gefunden`,
          "info",
        );
      } else {
        notificationStore.showMessage(
          `${data.exportedCount} LastSchrift-Transaktionen erfolgreich exportiert` +
            (typeof data.totalAmount === "number"
              ? ` (€${data.totalAmount.toFixed(2)})`
              : ""),
          "success",
        );
      }
      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim SEPA-Export: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public undoSEPAExport = async (lastSchriftIds: string[]) => {
    try {
      const { data } = await this.apiUnwrapped.undoSEPAExportForLastSchrifts({
        lastSchriftIds,
      });
      this.lastUpdate = new Date();

      if (data.errors && data.errors.length > 0) {
        notificationStore.showMessage(
          `SEPA-Export rückgängig gemacht für ${data.undoneCount} von ${data.totalRequested} Transaktionen. ${data.errors.length} Fehler aufgetreten.`,
          "warning",
        );
      } else {
        notificationStore.showMessage(
          `SEPA-Export erfolgreich rückgängig gemacht für ${data.undoneCount} Transaktionen`,
          "success",
        );
      }

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Rückgängigmachen des SEPA-Exports: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  public undoSEPAExportByDocument = async (documentId: string) => {
    try {
      const { data } = await this.apiUnwrapped.undoSEPAExportByDocument({
        documentId,
      });
      this.lastUpdate = new Date();

      notificationStore.showMessage(
        `SEPA-Export erfolgreich rückgängig gemacht`,
        "success",
      );

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Rückgängigmachen des SEPA-Exports: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  // ===== SEPA Credit Transfer Methods (pain.001.001.03) =====

  /**
   * Get all unexported credit transfers (refunds) for an account
   * Similar to getUnexportedLastSchrifts but for credit transfers
   */
  public getAllUnexportedCreditTransfersForAccount = async (
    accountId?: string,
  ) => {
    try {
      const { data } =
        await this.apiUnwrapped.getUnexportedCreditTransfers(accountId);
      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Abrufen der nicht exportierten Rückerstattungs-Transaktionen: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * Export selected credit transfers to SEPA pain.001.001.03 format and mark as exported
   * Uses batch endpoint with proper debtor/creditor roles (clinic = debtor for refunds)
   */
  public exportCreditTransfersBatch = async (
    creditTransferIds: string[],
    exportOptions: {
      messageId: string;
      debtorName: string;
      debtorIBAN: string;
      debtorBIC: string;
      debtorCreditorId?: string;
    },
  ) => {
    try {
      const { data } = await this.apiUnwrapped.exportCreditTransfersBatch({
        creditTransferIds,
        exportOptions,
      });
      this.lastUpdate = new Date();

      // Show success message with export details
      if (data.message) {
        notificationStore.showMessage(data.message, "success");
      } else {
        notificationStore.showMessage(
          `${data.exportedCount} Rückerstattungs-Transaktionen erfolgreich exportiert`,
          "success",
        );
      }

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim SEPA Credit Transfer Export: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * Undo SEPA credit transfer export for selected credit transfer IDs
   */
  public undoCreditTransferExport = async (creditTransferIds: string[]) => {
    try {
      const { data } = await this.apiUnwrapped.undoCreditTransferExport({
        creditTransferIds,
      });
      this.lastUpdate = new Date();

      if (data.errors && data.errors.length > 0) {
        notificationStore.showMessage(
          `SEPA Credit Transfer Export rückgängig gemacht für ${data.undoneCount} Transaktionen. ${data.errors.length} Fehler aufgetreten.`,
          "warning",
        );
      } else {
        notificationStore.showMessage(
          `SEPA Credit Transfer Export erfolgreich rückgängig gemacht für ${data.undoneCount} Transaktionen`,
          "success",
        );
      }

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Rückgängigmachen des SEPA Credit Transfer Exports: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * Undo SEPA credit transfer export by document ID
   */
  public undoCreditTransferExportByDocument = async (documentId: string) => {
    try {
      const { data } =
        await this.apiUnwrapped.undoCreditTransferExportByDocument({
          documentId,
        });
      this.lastUpdate = new Date();

      notificationStore.showMessage(
        `SEPA Credit Transfer Export erfolgreich rückgängig gemacht`,
        "success",
      );

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim Rückgängigmachen des SEPA Credit Transfer Exports: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * @deprecated Use exportCreditTransfersBatch instead
   * Old single-export endpoint for credit transfers
   */
  public exportCreditTransferToSEPA = async (
    request: SEPACreditTransferRequest,
  ) => {
    try {
      const { data } =
        await this.apiUnwrapped.exportCreditTransferToSEPA(request);
      this.lastUpdate = new Date();

      // Format total amount for display
      const totalAmount = request.transactions.reduce(
        (sum, t) => sum + t.amount,
        0,
      );
      const totalAmountFormatted = totalAmount.toFixed(2);

      notificationStore.showMessage(
        `SEPA Credit Transfer erfolgreich erstellt: ${data.exportedCount} Überweisungen (€${totalAmountFormatted})`,
        "success",
      );

      return data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      notificationStore.showMessage(
        `Fehler beim SEPA Credit Transfer Export: ${errorMessage}`,
        "error",
        error,
      );
      throw error;
    }
  };
}
